/**
 * O histórico de LOTES de edição e o desfazer (2026-10-22): o que a tela
 * diz de cada lote, sem render.
 *
 * O patch do lote é o que a pessoa pediu ("Polaridade → Positivo", "+ 2
 * subtemas"); o lote de desfazer carrega só `{undo_of: N}`. Aqui mora a
 * frase de cada um e a mensagem de desfecho do desfazer.
 */

import type { BulkEdit, BulkEditFailure, BulkListMode, BulkPatchWire } from "@/shared/api/api-client";
import { polarityLabel } from "@/shared/types/polarity";
import { APPROACH_CHOICES } from "./mention-filter-criteria";

const LIST_LABELS = {
  themes: ["Subtema", "Subtemas"],
  key_messages: ["Mensagem-chave", "Mensagens-chave"],
  spokespersons: ["Porta-voz", "Porta-vozes"]
} as const;

function listLine(key: keyof typeof LIST_LABELS, change: { mode: BulkListMode; ids: number[] }): string {
  const [one, many] = LIST_LABELS[key];
  const count = change.ids.length;
  const noun = count === 1 ? one : many;
  if (change.mode === "add") return `${noun}: + ${count}`;
  if (change.mode === "remove") return `${noun}: − ${count}`;
  return count === 0 ? `${many}: esvaziar` : `${many}: trocar por ${count}`;
}

/** As frases do patch, uma por dimensão tocada. Lote de desfazer vira
 *  uma frase só, apontando o lote desfeito. */
export function describeBulkPatch(patch: BulkPatchWire): string[] {
  if (patch.undo_of !== undefined) return [`Desfazer do lote #${patch.undo_of}`];
  const lines: string[] = [];
  if (patch.polarity) {
    lines.push(
      patch.polarity.value === null
        ? "Polaridade: remover"
        : `Polaridade → ${polarityLabel(patch.polarity.value)}`
    );
  }
  if (patch.approach) {
    const value = patch.approach.value;
    const found = APPROACH_CHOICES.find(choice => choice.value === value);
    lines.push(value === null ? "Abordagem: remover" : `Abordagem → ${found?.label ?? value}`);
  }
  if (patch.themes) lines.push(listLine("themes", patch.themes));
  if (patch.key_messages) lines.push(listLine("key_messages", patch.key_messages));
  if (patch.spokespersons) lines.push(listLine("spokespersons", patch.spokespersons));
  if (patch.highlight) lines.push("Subtema em destaque");
  if (patch.reviewed) lines.push(patch.reviewed.value ? "Marcar como revisada" : "Desmarcar revisão");
  if (patch.discarded) lines.push(patch.discarded.value ? "Descartar" : "Restaurar");
  return lines;
}

/** O lote ainda vai andar? */
export function isBulkRunning(edit: BulkEdit): boolean {
  return edit.status === "QUEUED" || edit.status === "STARTED";
}

const STATUS_LABELS: Record<BulkEdit["status"], string> = {
  QUEUED: "Na fila",
  STARTED: "Rodando",
  FINISHED: "Concluído",
  ERRORED: "Com erro"
};

export function bulkStatusLabel(status: BulkEdit["status"]): string {
  return STATUS_LABELS[status];
}

/** Tom do selo de status — as classes de `.upload-status`. */
export function bulkStatusTone(status: BulkEdit["status"]): "success" | "warning" | "" {
  if (status === "FINISHED") return "success";
  if (status === "ERRORED") return "warning";
  return "";
}

/** O que dizer quando um lote de DESFAZER termina: quantas voltaram e
 *  quantas ficaram de fora (alteradas depois do lote, por exemplo). */
export function undoOutcomeMessage(edit: BulkEdit): string {
  const { changed, failed } = edit.counters;
  const left = failed;
  const undone = `${changed.toLocaleString("pt-BR")} ${changed === 1 ? "desfeita" : "desfeitas"}`;
  const out = `${left.toLocaleString("pt-BR")} ${left === 1 ? "ficou" : "ficaram"} de fora`;
  return `Lote #${edit.undo_of_id ?? edit.patch.undo_of ?? "?"} desfeito: ${undone}, ${out}.`;
}

/** Os motivos de quem ficou de fora, uma linha por menção. */
export function failureLines(failures: BulkEditFailure[]): string[] {
  return failures.map(item => `Menção #${item.mention_id}: ${item.message ?? "erro não informado"}`);
}
